import { exec } from 'node:child_process';
import { promisify } from 'node:util';
import { cancel, isCancel, log, multiselect, outro, select, text } from '@clack/prompts';
import color from 'chalk';
import { Command, program } from 'commander';
import * as v from 'valibot';
import { confirmAction, intro, nextSteps, spinner } from '../utils/prompts';
const schema = v.object({
	verbose: v.boolean(),
	cwd: v.string(),
	stylesPaths: v.array(v.string()),
	yes: v.boolean(),
	angularVersion: v.optional(v.string())
});
import { existsSync, readFileSync } from 'node:fs';
import * as fsp from 'node:fs/promises';
import path from 'node:path';
import {
	DEFAULT_REGISTRY_HOST,
	DEFAULT_REPOS,
	INDEX_STYLE_BLOCKS,
	NGM_DEV_CLI_NAME,
	STYLE_BLOCKS
} from '../constants';
import * as ascii from '../utils/ascii';
import { getBlockFilePath, preloadBlocks, resolveTree } from '../utils/blocks';
import { type ProjectConfig, getProjectConfig, resolvePaths } from '../utils/config';
import * as registry from '../utils/registry-providers/internal';
import { _add } from './add';
import { addInProviders, findAppConfig } from '../utils/find-app-config';
import { languages } from '../utils/language-support';
import { loadFormatterConfig } from '../utils/format';
import prettier from 'prettier';
import { findProjectJsonFiles } from '../utils/nx-helpers';
import { getAngularVersion } from '../utils/angular-version';

const NOT_FOUND = 'NOT_FOUND';
const MATERIAL_SYMBOLS_LINK = `<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200" />`;
type Options = v.InferInput<typeof schema>;

export const setup = new Command('setup')
	.description(
		`Setup your project to be ready to use blocks from ${DEFAULT_REGISTRY_HOST}. This command will do following:

- Creates \`postcssrc.json\`
- Installs \`tailwindcss\` and \`@angular/material\`
- Adds needed styles in \`angular.json\`
- Configures Material Symbols`
	)
	.option('--verbose', 'Include debug logs.', false)
	.option('--yes', 'Skip prompts and use default options.', false)
	.option('--cwd [path]', 'The current working directory.', process.cwd())
	.option(
		'--angular-version [version]',
		'Angular version to be used for the block generation. If not provided, it will be inferred from the project. Supported versions are: 19, 20, 21.'
	)
	.action(async (opts) => {
		opts.stylesPaths = [];
		const options = v.parse(schema, opts);

		await intro();

		let steps = await _setup(options);
		steps = steps.concat([`Add blocks with ${color.cyan(`${NGM_DEV_CLI_NAME} add`)}!`]);
		steps = [...new Set(steps)].filter((step) => !!step);
		steps = steps.map((step, i) => `${i + 1}. ${step}`);
		if (steps.length > 0) process.stdout.write(nextSteps(steps));

		outro(color.green('All done!'));
	});

// Convert callback-based functions to promise-based
const execPromise = promisify(exec);

// Constants
const ANGULAR_MATERIAL_PACKAGE = '@angular/material';
const TAILWIND_PACKAGES = ['tailwindcss', '@tailwindcss/postcss', 'postcss'];

/**
 * Goal for this script is as below
 * 1. add "src/ngm-dev/styles/index.scss" to the styles array of the angular project, whether it is a angular.json or nx.json project.
 * 2. Install @angular/material and tailwindcss packages if not installed.
 * 3. Configure @angular/material in angular.json or nx.json project.
 * 4. Add needed styles files to the styles array of the angular project.
 * 5. Configure Material symbols in index.html and app.config.ts
 */
export const _setup = async (options: Options): Promise<string[]> => {
	const steps: string[] = [];
	const verbose = (msg: string) => {
		if (options.verbose) {
			console.info(`${ascii.INFO} ${msg}`);
		}
	};
	const loading = spinner({ verbose: options.verbose ? verbose : undefined });

	const configResult = getProjectConfig(options.cwd);

	let config: ProjectConfig;

	if (configResult.isErr()) {
		let shouldContinue = false;

		const response =
			options.yes ||
			(await confirmAction(
				`You don't have ${ascii.NGM_DEV_CLI} initialized in your project. Do you want to continue?`,
				false
			));

		if (isCancel(response)) {
			cancel('Canceled!');
			process.exit(0);
		}

		shouldContinue = response;

		if (!shouldContinue) {
			steps.push(`Initialize project using ${color.cyan.bold(`${NGM_DEV_CLI_NAME} init`)}`);
			return steps;
		}

		const stylesPath = await text({
			message: `Where would you like to add ${color.cyan('styles')}?`,
			placeholder: './src/app/ngm-dev-blocks/styles',
			initialValue: './src/app/ngm-dev-blocks/styles',
			defaultValue: './src/app/ngm-dev-blocks/styles',
			validate(value) {
				if (value.trim() === '') return 'Please provide a value';
			}
		});

		if (isCancel(stylesPath)) {
			cancel('Canceled!');
			process.exit(0);
		}

		// add default config used for default values in prompts
		config = {
			$schema: '',
			includeTests: false,
			watermark: true,
			paths: {
				styles: stylesPath,
				'*': './src/app/ngm-dev-blocks'
			},
			repos: DEFAULT_REPOS
		};
	} else {
		config = configResult.unwrap();
	}
	if (config.repos.length > 1 || config.repos.length === 0) {
		program.error('Repos not configured correctly');
	}
	const { prettierOptions } = await loadFormatterConfig({
		cwd: options.cwd
	});
	const repoPaths = [config.repos[0]];

	const resolvedRepos: registry.RegistryProviderState[] = (
		await registry.forEachPathGetProviderState(repoPaths, { noCache: true })
	).match(
		(val) => val,
		({ repo, message }) => {
			program.error(`Failed to get info for ${color.cyan(repo)}, \n${color.red(message)}`);
		}
	);

	verbose(`Resolved ${color.cyan(repoPaths.join(', '))}`);

	verbose(`Fetching blocks from ${color.cyan(repoPaths.join(', '))}`);

	const stylesSteps = await setupStyles(options, config);
	steps.push(...stylesSteps);

	const angularVersion = getAngularVersion({
		cwd: options.cwd,
		angularVersion: options.angularVersion,
		verbose
	});

	const manifests = (
		await registry.fetchManifests({
			repos: resolvedRepos,
			version: angularVersion
		})
	).match(
		(v) => v,
		({ repo, message }) => {
			program.error(`Failed to fetch manifests for ${color.cyan(repo)}, \n${color.red(message)}`);
		}
	);

	const blocksMap = registry.getRemoteBlocks(manifests);

	verbose(`Retrieved blocks from ${color.cyan(repoPaths.join(', '))}`);

	// for (const manifest of manifests) {
	// 	checkPreconditions(manifest.state, manifest.manifest, options.cwd);
	// }

	const styleBlockName = [INDEX_STYLE_BLOCKS];

	const styleBlocks = (await resolveTree(styleBlockName, blocksMap, resolvedRepos)).match(
		(val) => val,
		program.error
	);

	const resolvedPaths = resolvePaths(config.paths, options.cwd).match(
		(v) => v,
		(err) => program.error(color.red(err))
	);

	const preloadedStyleBlocks = preloadBlocks({
		blocks: styleBlocks,
		config,
		verbose: options.verbose,
		cwd: options.cwd,
		angularVersion
	});

	options.stylesPaths = [];

	for (const preloadedBlock of preloadedStyleBlocks) {
		const files = await preloadedBlock.files;

		for (const file of files) {
			const destPath = getBlockFilePath(
				file.name,
				preloadedBlock.block,
				resolvedPaths,
				options.cwd
			);
			options.stylesPaths.push(destPath);
		}
	}

	const packageManager = getPackageManager();
	log.info(`Detected package manager: ${color.cyan.bold(packageManager)}`);

	loading.start('Checking installed packages');

	// Check if all Tailwind packages are installed
	const missingTailwindPackages = TAILWIND_PACKAGES.filter((pkg) => !isPackageInstalled(pkg));

	loading.stop('Package check complete');

	// Install Tailwind packages if any are missing
	if (missingTailwindPackages.length > 0) {
		log.info(
			`Some Tailwind packages are missing: ${color.cyan.bold(missingTailwindPackages.join(', '))}`
		);

		// Ask for confirmation before installing Tailwind packages
		const confirmTailwind =
			options.yes ||
			(await confirmAction(
				`Do you want to install ${color.cyan.bold(missingTailwindPackages.join(', '))} using ${color.cyan.bold(packageManager)}?`,
				true
			));

		if (!confirmTailwind) {
			log.info('Skipping Tailwind packages installation');
			steps.push(
				`Install these Tailwind packages manually: ${color.cyan.bold(missingTailwindPackages.join(', '))}`
			);
		} else {
			try {
				await installPackages(missingTailwindPackages, packageManager, options, true);
			} catch (error) {
				log.error(color.red(`Failed to install Tailwind packages: ${error}`));
				steps.push(
					`Install these Tailwind packages manually: ${color.red(missingTailwindPackages.join(', '))}, and then run ${color.cyan.bold(`${NGM_DEV_CLI_NAME} setup`)} again.`
				);
				return steps;
			}
		}
	} else {
		log.info('All Tailwind packages are already installed');
	}

	const angularSuccess = await handleAngularProject(options, config, prettierOptions);
	steps.push(...angularSuccess.filter((step) => step !== NOT_FOUND));

	// try Nx project
	const nxSuccess = await handleNxProject(options, config, prettierOptions, packageManager);
	steps.push(...nxSuccess.filter((step) => step !== NOT_FOUND));

	outro(color.green('Setup completed successfully!'));

	return steps;
};

// Helper functions
async function fileExists(filePath: string): Promise<boolean> {
	try {
		await fsp.access(filePath);
		return true;
	} catch {
		return false;
	}
}

function isPackageInstalled(packageName: string): boolean {
	try {
		const packageJsonPath = path.join(process.cwd(), 'package.json');
		// Dynamic import instead of require
		const packageJson = JSON.parse(readFileSync(packageJsonPath, 'utf-8'));
		return !!(
			packageJson.dependencies?.[packageName] || packageJson.devDependencies?.[packageName]
		);
	} catch (error) {
		console.error(`Error checking if ${packageName} is installed:`, error);
		return false;
	}
}

function getPackageManager(): 'npm' | 'yarn' | 'pnpm' {
	try {
		// Check for yarn.lock or pnpm-lock.yaml
		if (existsSync(path.join(process.cwd(), 'yarn.lock'))) {
			return 'yarn';
		}
		if (existsSync(path.join(process.cwd(), 'pnpm-lock.yaml'))) {
			return 'pnpm';
		}
		return 'npm';
	} catch {
		return 'npm'; // Default to npm
	}
}

async function installPackages(
	packages: string[],
	packageManager: 'npm' | 'yarn' | 'pnpm',
	options: Options,
	force: boolean = false
): Promise<void> {
	const verbose = (msg: string) => {
		if (options.verbose) {
			console.info(`${ascii.INFO} ${msg}`);
		}
	};
	const command =
		packageManager === 'npm'
			? `npm install ${packages.join(' ')}${force ? ' --force' : ''}`
			: packageManager === 'yarn'
				? `yarn add ${packages.join(' ')}${force ? ' --force' : ''}`
				: `pnpm add ${packages.join(' ')}${force ? ' --force' : ''}`;
	const loading = spinner({ verbose: options.verbose ? verbose : undefined });
	loading.start(`${color.cyan.bold(command)}`);

	try {
		await execPromise(command);
		loading.stop(
			color.green(`Packages installed successfully: ${color.cyan.bold(packages.join(', '))}`)
		);
	} catch (error) {
		if (error instanceof Error) {
			loading.stop(color.red(`Error installing packages: ${error.message}`));
		}
		throw error;
	}
}

async function addAngularMaterial(projectName: string, options: Options): Promise<void> {
	const verbose = (msg: string) => {
		if (options.verbose) {
			console.info(`${ascii.INFO} ${msg}`);
		}
	};
	const loading = spinner({ verbose: options.verbose ? verbose : undefined });
	const command = `ng add ${ANGULAR_MATERIAL_PACKAGE} --theme custom --typography --defaults --project ${projectName} --skip-confirmation --non-interactive`;

	loading.start(`${color.cyan.bold(command)}`);

	try {
		await execPromise(command);
		loading.stop(
			`Angular Material installed successfully for project ${color.cyan.bold(projectName)}`
		);
	} catch (error) {
		if (error instanceof Error) {
			loading.stop(color.red(`Error installing Angular Material: ${error.message}`));
		}
		throw error;
	}
}

async function addAngularMaterialInNX(
	projectName: string,
	options: Options,
	packageManager: 'npm' | 'yarn' | 'pnpm'
): Promise<void> {
	const verbose = (msg: string) => {
		if (options.verbose) {
			console.info(`${ascii.INFO} ${msg}`);
		}
	};
	const loading = spinner({ verbose: options.verbose ? verbose : undefined });
	await installPackages([ANGULAR_MATERIAL_PACKAGE], packageManager, options);
	const command = `npx nx g ${ANGULAR_MATERIAL_PACKAGE}:ng-add --theme custom --typography --defaults --project ${projectName} --skip-confirmation --non-interactive`;

	loading.start(`${color.cyan.bold(command)}`);

	await execPromise(command);
	loading.stop(`Angular Material added successfully for project ${color.cyan.bold(projectName)}`);
}

// Main functions
async function handleAngularProject(
	options: Options,
	config: ProjectConfig,
	prettierOptions: prettier.Options | null
): Promise<string[]> {
	const steps: string[] = [];
	const angularJsonPath = path.join(process.cwd(), 'angular.json');

	if (!(await fileExists(angularJsonPath))) {
		log.info('angular.json not found, skipping Angular project setup.');
		steps.push(NOT_FOUND);
		return steps;
	}

	log.info('Found angular.json...');

	// Ask for confirmation before proceeding with Angular project setup
	const multipleResponse = options.yes
		? ['install', 'styles', 'symbols']
		: await multiselect({
				message: `Which steps would you like to configure? (${ascii.KEYBOARD_KEYS.ENTER} to confirm/skip, ${ascii.KEYBOARD_KEYS.SPACE} to select/deselect, ${ascii.KEYBOARD_KEYS.A} to select all)`,
				options: [
					{
						value: 'install',
						label: `Install ${color.cyan.bold(ANGULAR_MATERIAL_PACKAGE)} if not installed`
					},
					{
						value: 'styles',
						label: `Add needed styles to ${color.cyan.bold('angular.json')}'s styles array`
					},
					{
						value: 'symbols',
						label: `Add Material Symbols in ${color.cyan.bold('index.html')} and ${color.cyan.bold('app.config.ts')}`
					}
				],
				required: false
			});

	if (isCancel(multipleResponse)) {
		cancel('Canceled!');
		process.exit(0);
	}

	const confirmAngular = multipleResponse.length > 0;

	if (!confirmAngular) {
		log.info('Skipping Angular project configuration');
		steps.push(
			`Skipped Angular project configuration. You can run ${color.cyan.bold(`${NGM_DEV_CLI_NAME} setup`)} again to configure Angular project, or follow manual steps from https://ui.angular-material.dev/docs`
		);
		return steps;
	}

	// Read angular.json file
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	let angularJson: Record<string, any> = {};
	try {
		const content = await fsp.readFile(angularJsonPath, 'utf-8');
		angularJson = JSON.parse(content);
	} catch (error) {
		log.error(`Error reading angular.json: ${error}`);
		steps.push(
			`Could not read ${color.cyan.bold('angular.json')} file. Please check if it exists and is valid. Then run ${color.cyan.bold(`${NGM_DEV_CLI_NAME} setup`)} again.`
		);
		return steps;
	}

	// Find application projects
	const applicationProjects: string[] = [];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const projects: Record<string, any> = angularJson.projects || {};

	for (const [projectName, projectConfig] of Object.entries(projects)) {
		if (projectConfig.projectType === 'application') {
			applicationProjects.push(projectName);
		}
	}

	if (applicationProjects.length === 0) {
		log.info('No application projects found in angular.json, skipping.');
		steps.push(
			`No application projects found in ${color.cyan.bold('angular.json')}. Add one and then run ${color.cyan.bold(`${NGM_DEV_CLI_NAME} setup`)} again.`
		);
		return steps;
	}

	let selectedProjects: string[] = [];

	// If only one project, select it automatically
	if (applicationProjects.length === 1) {
		selectedProjects = applicationProjects;
		log.info(`Only one application project found: ${color.cyan.bold(selectedProjects[0])}`);
	} else {
		// Multiple projects found, ask user to select

		const selection = await select({
			message: 'Select projects to configure',
			options: [
				{ value: 'all', label: 'All projects' },
				...applicationProjects.map((project) => ({
					value: project,
					label: project
				}))
			],
			initialValue: 'all'
		});

		if (isCancel(selection)) {
			cancel('Setup cancelled.');
			return steps;
		}

		selectedProjects = selection === 'all' ? applicationProjects : [selection as string];
	}

	const changes: Record<string, string> = {};
	// Process each selected project
	for (const projectName of selectedProjects) {
		log.info(`Processing project: ${color.cyan.bold(projectName)}`);

		// Check and install Angular Material if needed
		if (multipleResponse.includes('install')) {
			if (!isPackageInstalled(ANGULAR_MATERIAL_PACKAGE)) {
				log.info(`${ANGULAR_MATERIAL_PACKAGE} not found`);

				try {
					await addAngularMaterial(projectName, options);
					// if angular material is installed, read the angular.json file again
					angularJson = JSON.parse(await fsp.readFile(angularJsonPath, 'utf-8'));
				} catch (error) {
					log.error(`Error installing Angular Material: ${error}`);
					steps.push(
						`Error installing ${color.cyan.bold(ANGULAR_MATERIAL_PACKAGE)}: ${error}.\nPlease manually install it.`
					);
					continue;
				}
			} else {
				log.info(`${ANGULAR_MATERIAL_PACKAGE} already installed.`);
			}
		} else {
			log.info(`Skipping ${color.cyan.bold(ANGULAR_MATERIAL_PACKAGE)} installation`);
			steps.push(
				`Skipped ${color.cyan.bold(ANGULAR_MATERIAL_PACKAGE)} installation. Please install it manually.`
			);
		}

		if (multipleResponse.includes('styles')) {
			// Update styles array
			const stylesArray =
				angularJson.projects[projectName]?.architect?.build?.options?.styles ?? [];

			// Check if our style is already in the array
			for (const stylePath of options.stylesPaths) {
				const relativeStylePath = path.relative(process.cwd(), stylePath).replace(/\\/g, '/');

				// check if relativeStylePath exists in current directory
				const exists = await fileExists(stylePath);

				if (!exists) {
					// get the block name from the relativeStylePath, for example for src/app/ngm-dev-blocks/styles/ngm-dev-blocks-styles.scss, the block name will be styles/ngm-dev-blocks-styles
					const blockName = relativeStylePath
						.split('/')
						.slice(-2)
						.join('/')
						.replace('.scss', '')
						.replace('.css', '');
					log.error(
						`Style file not found at path: ${relativeStylePath}. You can add needed styles files using ${color.cyan.bold(
							`${NGM_DEV_CLI_NAME} add ${blockName}`
						)}`
					);
					steps.push(
						`You can add needed styles files using ${color.cyan.bold(
							`${NGM_DEV_CLI_NAME} add ${blockName}`
						)}`
					);
					continue;
				}

				if (!stylesArray.includes(relativeStylePath)) {
					stylesArray.push(relativeStylePath);
					log.info(
						`Added ${color.cyan.bold(relativeStylePath)} to styles array for project ${color.cyan.bold(
							projectName
						)}`
					);
				} else {
					log.info(
						`${color.cyan.bold(relativeStylePath)} already exists in styles array for project ${color.cyan.bold(
							projectName
						)}`
					);
				}
			}

			// Update angular.json
			angularJson.projects[projectName].architect.build.options.styles = stylesArray;
			changes['angular.json'] = JSON.stringify(angularJson, null, 2);
		} else {
			log.info(`Skipping ${color.cyan.bold('angular.json')} update`);
		}

		// Check if material icons are already configured in app.config.ts
		if (multipleResponse.includes('symbols')) {
			const appConfigPath = findAppConfig(
				path.join(process.cwd(), angularJson.projects[projectName].sourceRoot ?? '')
			);
			if (appConfigPath) {
				const appConfig = await fsp.readFile(appConfigPath, 'utf-8');
				if (appConfig.includes('material-symbols-outlined')) {
					log.info(`Material icons are already configured in ${color.cyan.bold(appConfigPath)}`);
				} else {
					log.info(
						`Adding Material Symbols in ${color.cyan.bold('index.html')} and ${color.cyan.bold('app.config.ts')}`
					);

					const indexHTMLPath = path.join(
						process.cwd(),
						angularJson.projects[projectName].sourceRoot ?? '',
						'index.html'
					);

					if (await fileExists(indexHTMLPath)) {
						const indexHTML = await fsp.readFile(indexHTMLPath, 'utf-8');
						if (indexHTML.includes('Material+Symbols+Outlined')) {
							log.info(
								`Material symbols are already configured in ${color.cyan.bold(indexHTMLPath)}`
							);
						} else {
							const updatedIndexHTML = indexHTML.replace(
								'</head>',
								`${MATERIAL_SYMBOLS_LINK}\n</head>`
							);
							changes[indexHTMLPath] = updatedIndexHTML;
						}
					} else {
						steps.push(
							`${color.cyan.bold(indexHTMLPath)} not found. Manually add the following to your project's index.html:\n${color.cyan.bold(MATERIAL_SYMBOLS_LINK)}`
						);
					}

					let updatedAppConfig = appConfig;
					// import statement
					const importStatement =
						'import { MAT_ICON_DEFAULT_OPTIONS } from "@angular/material/icon";';

					const provider = `{
      provide: MAT_ICON_DEFAULT_OPTIONS,
      useValue: {
        fontSet: 'material-symbols-outlined',
      },
    }`;

					if (!appConfig.includes('MAT_ICON_DEFAULT_OPTIONS')) {
						updatedAppConfig = `${importStatement}\n${appConfig}`;
					}

					updatedAppConfig = addInProviders(updatedAppConfig, [provider]);
					changes[appConfigPath] = updatedAppConfig;
				}
			} else {
				log.info(
					`${appConfigPath} not found, skipping material symbols configuration. Manually configure Material Symbols, learn more at https://ui.angular-material.dev/docs`
				);
			}
		} else {
			log.info(`Skipping Material Symbols configuration`);
		}
	}

	if (Object.keys(changes).length === 0) {
		log.info('No changes needed for any projects');
		return steps;
	}

	const fileSuccessFullyUpdated: string[] = [];

	for (const [filePath, content] of Object.entries(changes)) {
		const relativeFilePath = path.relative(process.cwd(), filePath);
		try {
			const lang = languages.find((lang) => lang.matches(filePath));
			let updatedContent = content;
			if (lang) {
				updatedContent = await lang.format(content, {
					filePath,
					formatter: config.formatter,
					prettierOptions
				});
			}
			await fsp.writeFile(filePath, updatedContent);
			fileSuccessFullyUpdated.push(relativeFilePath);
		} catch (error) {
			log.error(`Error updating ${color.cyan.bold(relativeFilePath)}: ${error}`);
		}
	}

	if (fileSuccessFullyUpdated.length > 0) {
		log.success(
			`Successfully updated files: ${color.cyan.bold(fileSuccessFullyUpdated.join(', '))}`
		);
	}

	return steps;
}

async function handleNxProject(
	options: Options,
	config: ProjectConfig,
	prettierOptions: prettier.Options | null,
	packageManager: 'npm' | 'yarn' | 'pnpm'
): Promise<string[]> {
	const steps: string[] = [];
	const nxJsonPath = path.join(process.cwd(), 'nx.json');

	if (!(await fileExists(nxJsonPath))) {
		log.info('nx.json not found, skipping Nx project setup.');
		steps.push(NOT_FOUND);
		return steps;
	}

	log.info('Found nx.json...');

	const multipleResponse = options.yes
		? ['install', 'styles', 'symbols']
		: await multiselect({
				message: `Which steps would you like to configure? (${ascii.KEYBOARD_KEYS.ENTER} to confirm/skip, ${ascii.KEYBOARD_KEYS.SPACE} to select/deselect, ${ascii.KEYBOARD_KEYS.A} to select all)`,
				options: [
					{
						value: 'install',
						label: `Install ${color.cyan.bold(ANGULAR_MATERIAL_PACKAGE)} if not installed`
					},
					{
						value: 'styles',
						label: `Add needed styles to ${color.cyan.bold('project.json')}'s styles array`
					},
					{
						value: 'symbols',
						label: `Add Material Symbols in ${color.cyan.bold('index.html')} and ${color.cyan.bold('app.config.ts')}`
					}
				],
				required: false
			});

	if (isCancel(multipleResponse)) {
		cancel('Setup cancelled.');
		return steps;
	}

	const confirmNx = multipleResponse.length > 0;

	if (!confirmNx) {
		log.info('Skipping Nx project configuration');
		steps.push(NOT_FOUND);
		steps.push(
			`Skipped Nx project configuration. You can run ${color.cyan.bold(`${NGM_DEV_CLI_NAME} setup`)} again to configure Nx project, or follow manual steps from https://ui.angular-material.dev/docs`
		);
		return steps;
	}

	if (multipleResponse.length > 0) {
		// Find project.json files
		let projectJsonFiles: string[] = [];
		try {
			projectJsonFiles = await findProjectJsonFiles(options.cwd);
		} catch (error) {
			log.error(`Error finding project.json files: ${error}`);
			steps.push(
				`Error finding project.json files. Please check if they exist and are valid. Then run ${color.cyan.bold(`${NGM_DEV_CLI_NAME} setup`)} again.`
			);
			return steps;
		}

		if (projectJsonFiles.length === 0) {
			log.error('No project.json files found');
			steps.push(
				`No project.json files found. Add an angular project and then run ${color.cyan.bold(`${NGM_DEV_CLI_NAME} setup`)} again.`
			);
			return steps;
		}

		const changes: Record<string, string> = {};
		for (const filePath of projectJsonFiles) {
			try {
				const content = await fsp.readFile(filePath, 'utf-8');
				let projectJson = JSON.parse(content);
				const isProjectAngular =
					projectJson.projectType === 'application' &&
					(projectJson.targets?.build?.executor as string).includes('angular');

				if (!isProjectAngular) {
					continue;
				}

				const projectName = projectJson.name;

				log.info(`Found project.json for ${color.cyan.bold(projectName)} at ${filePath}`);

				// Check and install Angular Material if needed
				if (multipleResponse.includes('install')) {
					if (!isPackageInstalled(ANGULAR_MATERIAL_PACKAGE)) {
						log.info(`${ANGULAR_MATERIAL_PACKAGE} not found`);
						try {
							await addAngularMaterialInNX(projectName, options, packageManager);
							// if angular material is installed, read the project.json file again
							projectJson = JSON.parse(await fsp.readFile(filePath, 'utf-8'));
						} catch (error) {
							log.error(`Error installing Angular Material: ${error}`);
							steps.push(
								`Error installing ${color.cyan.bold(ANGULAR_MATERIAL_PACKAGE)} for project ${color.cyan.bold(
									projectName
								)}. Please install it manually and then run ${color.cyan.bold(`${NGM_DEV_CLI_NAME} setup`)} again.`
							);
							return steps;
						}
					} else {
						log.info(`${ANGULAR_MATERIAL_PACKAGE} already installed.`);
					}
				} else {
					log.info(`Skipping ${color.cyan.bold(ANGULAR_MATERIAL_PACKAGE)} installation`);
				}

				if (multipleResponse.includes('styles')) {
					// Find styles array in project.json
					const stylesArray = projectJson.targets?.build?.options?.styles ?? [];

					// Check if our style is already in the array
					for (const stylePath of options.stylesPaths) {
						// relative path with forward slashes
						const relativeStylePath = path.relative(process.cwd(), stylePath).replace(/\\/g, '/');

						// check if relativeStylePath exists in current directory
						const exists = await fileExists(stylePath);

						if (!exists) {
							// get the block name from the relativeStylePath, for example for src/app/ngm-dev-blocks/styles/ngm-dev-blocks-styles.scss, the block name will be styles/ngm-dev-blocks-styles
							const blockName = relativeStylePath
								.split('/')
								.slice(-2)
								.join('/')
								.replace('.scss', '')
								.replace('.css', '');
							log.error(
								`Style file not found at path: ${relativeStylePath}. You can add needed styles files using ${color.cyan.bold(
									`${NGM_DEV_CLI_NAME} add ${blockName}`
								)}`
							);
							continue;
						}

						if (!stylesArray.includes(relativeStylePath)) {
							stylesArray.push(relativeStylePath);
							log.info(
								`Added ${color.cyan.bold(relativeStylePath)} to styles array for project ${color.cyan.bold(
									projectName
								)}`
							);
						} else {
							log.info(
								`${color.cyan.bold(relativeStylePath)} already exists in styles array for project ${color.cyan.bold(
									projectName
								)}`
							);
						}
					}

					// Update project.json
					projectJson.targets.build.options.styles = stylesArray;
					changes[filePath] = JSON.stringify(projectJson, null, 2);
				} else {
					log.info(`Skipping ${color.cyan.bold('project.json')} update`);
				}

				if (multipleResponse.includes('symbols')) {
					// Check if material icons are already configured in app.config.ts
					const appConfigPath = findAppConfig(
						path.join(process.cwd(), projectJson.sourceRoot ?? '')
					);
					if (appConfigPath) {
						const appConfig = await fsp.readFile(appConfigPath, 'utf-8');
						if (appConfig.includes('material-symbols-outlined')) {
							log.info(
								`Material icons are already configured in ${color.cyan.bold(appConfigPath)}`
							);
						} else {
							log.info(
								`Adding Material Symbols in ${color.cyan.bold('index.html')} and ${color.cyan.bold('app.config.ts')}`
							);

							const indexHTMLPath = path.join(
								process.cwd(),
								projectJson.sourceRoot ?? '',
								'index.html'
							);

							if (await fileExists(indexHTMLPath)) {
								const indexHTML = await fsp.readFile(indexHTMLPath, 'utf-8');
								if (indexHTML.includes('Material+Symbols+Outlined')) {
									log.info(
										`Material symbols are already configured in ${color.cyan.bold(indexHTMLPath)}`
									);
								} else {
									const updatedIndexHTML = indexHTML.replace(
										'</head>',
										`${MATERIAL_SYMBOLS_LINK}\n</head>`
									);
									changes[indexHTMLPath] = updatedIndexHTML;
								}
							} else {
								steps.push(
									`${color.cyan.bold(indexHTMLPath)} not found. Manually add the following to your project's index.html:\n${color.cyan.bold(MATERIAL_SYMBOLS_LINK)}`
								);
							}

							let updatedAppConfig = appConfig;
							// import statement
							const importStatement =
								'import { MAT_ICON_DEFAULT_OPTIONS } from "@angular/material/icon";';

							const provider = `{
	  provide: MAT_ICON_DEFAULT_OPTIONS,
	  useValue: {
		fontSet: 'material-symbols-outlined',
	  },
	}`;

							if (!appConfig.includes('MAT_ICON_DEFAULT_OPTIONS')) {
								updatedAppConfig = `${importStatement}\n${appConfig}`;
							}

							updatedAppConfig = addInProviders(updatedAppConfig, [provider]);
							changes[appConfigPath] = updatedAppConfig;
						}
					} else {
						log.info(
							`${appConfigPath} not found, skipping material symbols configuration. Manually configure Material Symbols, learn more at https://ui.angular-material.dev/docs`
						);
					}
				} else {
					log.info(`Skipping Material Symbols configuration`);
				}
			} catch (error) {
				log.warning(`Error reading project.json at ${filePath}: ${error}`);
			}
		}

		if (Object.keys(changes).length === 0) {
			log.info('No changes needed for any projects');
			return steps;
		}

		const fileSuccessFullyUpdated: string[] = [];

		for (const [filePath, content] of Object.entries(changes)) {
			const relativeFilePath = path.relative(process.cwd(), filePath);
			try {
				const lang = languages.find((lang) => lang.matches(filePath));
				let updatedContent = content;
				if (lang) {
					updatedContent = await lang.format(content, {
						filePath,
						formatter: config.formatter,
						prettierOptions
					});
				}
				await fsp.writeFile(filePath, updatedContent);
				fileSuccessFullyUpdated.push(relativeFilePath);
			} catch (error) {
				log.error(`Error updating ${color.cyan.bold(relativeFilePath)}: ${error}`);
			}
		}

		if (fileSuccessFullyUpdated.length > 0) {
			log.success(
				`Successfully updated files: ${color.cyan.bold(fileSuccessFullyUpdated.join(', '))}`
			);
		}
	}
	return steps;
}

async function setupStyles(options: Options, config: ProjectConfig): Promise<string[]> {
	const steps: string[] = [];
	const stylesFiles = STYLE_BLOCKS;

	const confirmStyles =
		options.yes ||
		(await confirmAction(
			`Do you want CLI to add ${color.cyan.bold(stylesFiles.join(', '))} to your project? Note that all of these are needed for the blocks to work properly.`,
			true
		));

	if (!confirmStyles) {
		log.info('Skipping styles setup');
		steps.push(
			`Skipped styles setup. You can add needed styles files using ${color.cyan.bold(
				`${NGM_DEV_CLI_NAME} add ${stylesFiles.join(' ')}`
			)}`
		);
		return steps;
	}

	await Promise.all(
		stylesFiles.map(async (file) => {
			await _add([file], {
				allow: true,
				verbose: false,
				cwd: options.cwd,
				yes: true,
				expand: false,
				maxUnchanged: 3,
				skipPreconditions: true,
				silent: true,
				config
			});
			await new Promise((resolve) => setTimeout(resolve, 100));
		})
	);

	log.success(
		`Styles setup complete. Added ${color.cyan(stylesFiles.join(', '))} to your project.`
	);

	return steps;
}
